import {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {
    Box,
    Button,
    Chip,
    FormControl,
    IconButton,
    InputAdornment,
    InputBase,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Stack,
    Typography
} from '@mui/material';
import {alpha} from '@mui/material/styles';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

import MessageBox from '../components/common/box/MessageBox';
import IdeaDetailModal from '../components/idea/IdeaDetailModal';
import {readIdeaList, readIdeaYears} from '../modules/idea/idea';
import {SERVER_URL} from '../api/url';

const CATEGORY_OPTIONS = [
    { value: 'AUTONOMOUS', label: '자율제안' },
    { value: 'DESIGNATED', label: '지정주제' },
    { value: 'CITIZEN', label: '시민대상' },
    { value: 'REGULATION', label: '규제' },
    { value: 'ADMIN', label: '행정' },
    { value: 'SPACE', label: '공간' },
    { value: 'BUSINESS', label: '사업' },
    { value: 'ETC', label: '기타' },
    { value: 'WORKSHOP', label: '워크숍' },
    { value: 'UNCLASSIFIED', label: '미분류' }
];

const STATUS_OPTIONS = [
    { value: 'NORMAL', label: '정상' },
    { value: 'REFERENCE', label: '참고' },
    { value: 'REJECTED', label: '추진불가' },
    { value: 'CARRIED_OVER', label: '이월' }
];

const STATUS_COLOR = {
    '정상': 'success',
    '참고': 'info',
    '추진불가': 'error',
    '이월': 'warning'
};

const PAGE_SIZE = 20;

const getMetaParts = (registeredAt, department, proposer) => {
    const parts = [];
    if (registeredAt) parts.push(registeredAt);
    if (department && department !== '-') parts.push(department);
    if (proposer && proposer !== '-') parts.push(proposer);
    return parts;
};

const triggerDownload = (url, fileName) => {
    const a = document.createElement('a');
    a.href = url;
    if (fileName) a.download = fileName;
    a.target = '_blank';
    a.rel = 'noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
};

export default function IdeaPage() {
    const dispatch = useDispatch();

    const { listResponse, listErrorResponse, yearsResponse } = useSelector((state) => state.idea);
    const listLoading = useSelector(({ loading }) => loading['idea/READ_IDEA_LIST']);

    const yearOptions = useMemo(
        () => (yearsResponse ?? []).map((y) => ({ value: y, label: `${y}년` })),
        [yearsResponse]
    );

    const [filters, setFilters] = useState({ year: '', category: '', status: '' });
    const [keywordInput, setKeywordInput] = useState('');
    const [appliedKeyword, setAppliedKeyword] = useState('');
    const [page, setPage] = useState(1);
    const [selectedIdeaId, setSelectedIdeaId] = useState(null);

    const handleClickDetail = (ideaId) => (e) => {
        e.stopPropagation();
        setSelectedIdeaId(ideaId);
    };

    const handleClickDownload = (downloadUrl) => (e) => {
        e.stopPropagation();
        if (!downloadUrl) return;
        triggerDownload(`${SERVER_URL}${downloadUrl}`);
    };

    const handleChangeFilter = (name) => (e) => {
        setFilters((prev) => ({ ...prev, [name]: e.target.value }));
        setPage(1);
    };

    const handleSubmitKeyword = (e) => {
        e?.preventDefault?.();
        setAppliedKeyword(keywordInput.trim());
        setPage(1);
    };

    const handleChangePage = (_, value) => {
        setPage(value);
    };

    const handleReload = () => {
        dispatch(readIdeaList({
            year: filters.year,
            category: filters.category,
            status: filters.status,
            keyword: appliedKeyword,
            page,
            pagesize: PAGE_SIZE
        }));
    };

    useEffect(() => {
        dispatch(readIdeaList({
            year: filters.year,
            category: filters.category,
            status: filters.status,
            keyword: appliedKeyword,
            page,
            pagesize: PAGE_SIZE
        }));
    }, [dispatch, filters.year, filters.category, filters.status, appliedKeyword, page]);

    useEffect(() => {
        dispatch(readIdeaYears());
    }, [dispatch]);

    const content = listResponse?.content ?? [];
    const pagination = listResponse?.pagination;
    const totalPages = pagination?.totalPages ?? 0;
    const totalLength = pagination?.totalLength ?? 0;

    const isEmpty = !listLoading && !listErrorResponse && content.length === 0;
    const hasError = !listLoading && listErrorResponse;

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', p: 3, gap: 2, overflow: 'hidden' }}>
            <Stack direction='row' justifyContent='space-between' alignItems='baseline'>
                <Typography variant='h5' sx={{ fontWeight: 700, color: '#1F1B3A' }}>
                    창의혁신아이디어
                </Typography>
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                    총 {totalLength.toLocaleString()}건
                </Typography>
            </Stack>

            <Stack
                component='form'
                onSubmit={handleSubmitKeyword}
                direction='row'
                spacing={1}
                alignItems='center'
                sx={{ flexWrap: 'wrap', rowGap: 1 }}
            >
                <Box sx={{ flex: 1, minWidth: 180 }}>
                    <InputBase
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSubmitKeyword();
                            }
                        }}
                        placeholder='제목·요약 검색'
                        fullWidth
                        startAdornment={
                            <InputAdornment position='start' sx={{ ml: 1, color: 'action.active' }}>
                                <SearchRoundedIcon fontSize='small' />
                            </InputAdornment>
                        }
                        endAdornment={
                            keywordInput &&
                            <InputAdornment position='end'>
                                <IconButton
                                    size='small'
                                    onClick={() => {
                                        setKeywordInput('');
                                        setAppliedKeyword('');
                                        setPage(1);
                                    }}
                                    sx={{ mr: 0.5 }}
                                >
                                    <CloseRoundedIcon fontSize='small' />
                                </IconButton>
                            </InputAdornment>
                        }
                        sx={{
                            height: 36,
                            border: '1px solid',
                            borderColor: 'elevation.outlined',
                            borderRadius: '8px',
                            backgroundColor: '#FFF',
                            '& .MuiInputBase-input': { typography: 'body2', py: 0 }
                        }}
                    />
                </Box>
                <FilterSelect
                    value={filters.category}
                    onChange={handleChangeFilter('category')}
                    placeholder='분류 전체'
                    options={CATEGORY_OPTIONS}
                    minWidth={130}
                />
                <FilterSelect
                    value={filters.status}
                    onChange={handleChangeFilter('status')}
                    placeholder='상태 전체'
                    options={STATUS_OPTIONS}
                    minWidth={120}
                />
                <FilterSelect
                    value={filters.year}
                    onChange={handleChangeFilter('year')}
                    placeholder='연도 전체'
                    options={yearOptions}
                    minWidth={110}
                />
            </Stack>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.25, pr: 0.5 }}>
                {listLoading &&
                    <Paper variant='outlined' sx={{ p: 4, borderRadius: '10px' }}>
                        <MessageBox type='LOADING' loadingSize={32} />
                    </Paper>
                }
                {hasError &&
                    <Paper variant='outlined' sx={{ p: 4, borderRadius: '10px' }}>
                        <MessageBox
                            type='ERROR'
                            Icon={WarningRoundedIcon}
                            message='목록을 불러오는 중 오류 발생'
                            handleReload={handleReload}
                        />
                    </Paper>
                }
                {isEmpty &&
                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <MessageBox
                            Icon={LightbulbOutlinedIcon}
                            message='조건에 맞는 아이디어가 없습니다'
                        />
                    </Box>
                }
                {!listLoading && !hasError && content.map((item) => (
                    <Paper
                        key={item.id}
                        variant='outlined'
                        sx={{
                            p: 2,
                            borderRadius: '10px',
                            transition: 'border-color 120ms, box-shadow 120ms',
                            '&:hover': {
                                borderColor: 'primary.main',
                                boxShadow: '0 2px 8px rgba(31, 27, 58, 0.08)'
                            }
                        }}
                    >
                        <Stack spacing={1}>
                            <Stack direction='row' spacing={0.75} alignItems='center'>
                                <Chip
                                    label={item.category}
                                    size='small'
                                    sx={(theme) => ({
                                        height: 22,
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                        color: theme.palette.primary.dark
                                    })}
                                />
                                <Chip
                                    label={item.status}
                                    size='small'
                                    sx={(theme) => {
                                        const key = STATUS_COLOR[item.status] ?? 'primary';
                                        const p = theme.palette[key];
                                        return {
                                            height: 22,
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            backgroundColor: alpha(p.main, 0.12),
                                            color: p.dark
                                        };
                                    }}
                                />
                                <Box sx={{ flex: 1 }} />
                                <Stack direction='row' spacing={0.75} alignItems='center'>
                                    <Button
                                        size='small'
                                        variant='text'
                                        startIcon={<VisibilityRoundedIcon fontSize='small' />}
                                        onClick={handleClickDetail(item.id)}
                                        sx={{
                                            minWidth: 0,
                                            px: 1,
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                            '&:hover': { color: 'primary.main', backgroundColor: (t) => alpha(t.palette.primary.main, 0.08) }
                                        }}
                                    >
                                        상세보기
                                    </Button>
                                    <Button
                                        size='small'
                                        variant='text'
                                        startIcon={<DownloadRoundedIcon fontSize='small' />}
                                        disabled={!item.downloadUrl}
                                        onClick={handleClickDownload(item.downloadUrl)}
                                        title={item.downloadUrl ? '원본 문서 다운로드' : '원본 문서 없음'}
                                        sx={{
                                            minWidth: 0,
                                            px: 1,
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                            '&:hover': { color: 'primary.main', backgroundColor: (t) => alpha(t.palette.primary.main, 0.08) }
                                        }}
                                    >
                                        다운로드
                                    </Button>
                                </Stack>
                            </Stack>
                            <Typography
                                variant='subtitle1'
                                sx={{
                                    fontWeight: 700,
                                    color: '#1F1B3A',
                                    lineHeight: 1.4,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical'
                                }}
                            >
                                {item.title}
                            </Typography>
                            {item.summary && item.summary !== '-' &&
                                <Typography
                                    variant='body2'
                                    sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.5,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical'
                                    }}
                                >
                                    {item.summary}
                                </Typography>
                            }
                            <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                {(() => {
                                    const parts = getMetaParts(item.registeredAt, item.department, item.proposer);
                                    if (parts.length === 0) return '작성자 미표시';
                                    return parts.map((p, i) => (
                                        <span key={i}>
                                            {i > 0 && <span style={{ color: '#D8D5E0', margin: '0 8px' }}>|</span>}
                                            {p}
                                        </span>
                                    ));
                                })()}
                            </Typography>
                        </Stack>
                    </Paper>
                ))}
            </Box>

            {totalPages > 1 &&
                <Stack direction='row' justifyContent='center' sx={{ pt: 1 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handleChangePage}
                        color='primary'
                        shape='rounded'
                        size='small'
                    />
                </Stack>
            }

            <IdeaDetailModal
                open={selectedIdeaId !== null}
                ideaId={selectedIdeaId}
                onClose={() => setSelectedIdeaId(null)}
            />
        </Box>
    );
}

function FilterSelect({ value, onChange, placeholder, options, minWidth }) {
    const renderValue = useMemo(() => (selected) => {
        if (selected === '' || selected === null || selected === undefined) {
            return <Typography variant='body2' sx={{ color: 'text.disabled' }}>{placeholder}</Typography>;
        }
        const found = options.find((o) => String(o.value) === String(selected));
        return <Typography variant='body2'>{found?.label ?? selected}</Typography>;
    }, [options, placeholder]);

    return (
        <FormControl size='small' sx={{ minWidth }}>
            <Select
                value={value}
                onChange={onChange}
                displayEmpty
                renderValue={renderValue}
                sx={{
                    height: 36,
                    backgroundColor: '#FFF',
                    borderRadius: '8px',
                    '& .MuiSelect-select': { py: 0, display: 'flex', alignItems: 'center' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'elevation.outlined' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'elevation.outlined' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'elevation.outlined', borderWidth: 1 }
                }}
            >
                <MenuItem value=''>
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>{placeholder}</Typography>
                </MenuItem>
                {options.map((opt) => (
                    <MenuItem key={String(opt.value)} value={opt.value}>
                        <Typography variant='body2'>{opt.label}</Typography>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
